import { Box } from '@mui/material';
import { FC, PropsWithChildren } from 'react';
import { RoundedPaper } from '../../../atomic';

const CONTENT_BOX_PADDING = 24;

export type ContentBoxProps = {
  headComponent?: JSX.Element;
  footerComponent?: JSX.Element;
  sidebarComponent?: JSX.Element;
  sidebarComponentWidth?: string;
  paddingTop?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  paddingRight?: string;
  className?: string;
};

export const ContentBox: FC<PropsWithChildren<ContentBoxProps>> = ({
  headComponent,
  footerComponent,
  sidebarComponent,
  sidebarComponentWidth,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  children,
  className
}) => {
  const padding =
    `${paddingTop ?? CONTENT_BOX_PADDING + 'px'} ` +
    `${paddingRight ?? CONTENT_BOX_PADDING + 'px'} ` +
    `${paddingBottom ?? CONTENT_BOX_PADDING + 'px'} ` +
    `${paddingLeft ?? CONTENT_BOX_PADDING + 'px'} `;

  return (
    <RoundedPaper
      className={className}
      sx={{
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
          maxHeight: '6px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
          borderRadius: '8px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'grey.400',
          borderRadius: '8px',
          border: 'none'
        }
      }}
    >
      <Box flexGrow={1} sx={{ display: 'flex', flexDirection: 'column' }}>
        {headComponent}
        <Box display="flex" flexGrow={1} sx={{ overflowY: 'hidden' }}>
          {sidebarComponent && (
            <Box
              sx={(theme) => ({
                transitionProperty: 'width, padding-left, padding-right, border-color',
                transitionDuration: '1s',
                width: sidebarComponentWidth || '320px',
                paddingLeft:
                  sidebarComponentWidth === '0' ? 0 : `${paddingLeft ?? CONTENT_BOX_PADDING}px`,
                paddingRight:
                  sidebarComponentWidth === '0' ? 0 : `${paddingRight ?? CONTENT_BOX_PADDING}px`,
                paddingTop: `${paddingTop ?? CONTENT_BOX_PADDING}px`,
                paddingBottom: `${paddingBottom ?? CONTENT_BOX_PADDING}px`,
                overflowY: 'auto',
                boxSizing: 'border-box',
                flexShrink: 0,
                borderColor:
                  sidebarComponentWidth === '0' ? 'transparent' : theme.palette.grey['50'],
                borderRightWidth: '1px',
                borderRightStyle: sidebarComponent ? 'solid' : 'hidden',
                overflow: 'hidden'
              })}
            >
              {sidebarComponent}
            </Box>
          )}
          <Box
            flexGrow={1}
            sx={{
              padding: padding,
              overflowY: 'auto'
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
      {footerComponent && (
        <Box
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.grey['50']}`
          })}
          className="p-6"
        >
          {footerComponent}
        </Box>
      )}
    </RoundedPaper>
  );
};
